import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Target,
  AlertTriangle,
  Clock,
  Users,
  TrendingUp,
  TrendingDown,
  Phone,
  Mail,
  MapPin,
  Building,
  MessageSquare,
  MessageCircle,
  ChevronRight,
  MoreVertical,
  Plus,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  PauseCircle,
  ArrowRight,
  Calendar,
  Euro,
  Filter,
  Eye,
  Bell,
  Send,
  BarChart3,
  Archive,
  Trash2,
  RotateCcw,
  X,
  Search,
  LayoutList,
  LayoutGrid,
  Activity,
  Timer,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import CommercialReportPage from '@/pages/CommercialReportPage';

const CONTACT_ICONS = {
  telephone: Phone,
  email: Mail,
  visite_client: MapPin,
  visite_showroom: Building,
  sms: MessageSquare,
  whatsapp: MessageCircle,
};

const OpportunitiesPage = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('pipeline');
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [filterStage, setFilterStage] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'kanban'

  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Dialog states
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [activityForm, setActivityForm] = useState({
    contact_type: '',
    contact_result: '',
    notes: '',
    next_action_date: '',
    new_stage: '',
  });
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({
    reason: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchDashboard();
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [filterStage, filterStatus, showOverdueOnly]);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/opportunities/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/opportunities/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchOpportunities = async () => {
    setLoading(true);
    setSelectedIds(new Set()); // reset sélection au rechargement
    try {
      const params = new URLSearchParams();
      if (filterStage !== 'all') params.append('stage', filterStage);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (showOverdueOnly) params.append('overdue_only', 'true');
      
      const response = await api.get(`/opportunities/?${params.toString()}`);
      setOpportunities(response.data.opportunities || []);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- Sélection multiple ---
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === opportunities.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(opportunities.map(o => o.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const labels = { archive: 'archiver', delete: 'supprimer', reopen: 'rouvrir' };
    if (action === 'delete' && !window.confirm(`Supprimer définitivement ${ids.length} opportunité(s) ? Cette action est irréversible.`)) return;

    setBulkLoading(true);
    try {
      const res = await api.patch('/opportunities/bulk-action', { ids, action });
      const count = res.data.updated ?? res.data.deleted ?? ids.length;
      toast.success(`${count} opportunité(s) : ${labels[action]} avec succès`);
      setSelectedIds(new Set());
      fetchOpportunities();
      fetchDashboard();
    } catch (err) {
      toast.error(`Erreur lors de l'action en masse`);
    } finally {
      setBulkLoading(false);
    }
  };

  // Opportunités filtrées par recherche
  const filteredOpportunities = opportunities.filter(opp => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      opp.client?.name?.toLowerCase().includes(q) ||
      opp.client?.phone?.includes(q) ||
      opp.client?.email?.toLowerCase().includes(q) ||
      opp.client?.company?.toLowerCase().includes(q) ||
      opp.title?.toLowerCase().includes(q)
    );
  });

  const openActivityDialog = (opp, isStageChange = false) => {
    setSelectedOpp(opp);
    setActivityForm({
      contact_type: '',
      contact_result: '',
      notes: '',
      next_action_date: '',
      new_stage: isStageChange ? getNextStage(opp.stage)?.id || '' : '',
    });
    setActivityDialogOpen(true);
  };

  const openCloseDialog = (opp) => {
    setSelectedOpp(opp);
    setCloseForm({ reason: '', notes: '' });
    setCloseDialogOpen(true);
  };

  const getNextStage = (currentStage) => {
    if (!config) return null;
    const currentIndex = config.stages.findIndex(s => s.id === currentStage);
    if (currentIndex < config.stages.length - 1) {
      return config.stages[currentIndex + 1];
    }
    return null;
  };

  const handleLogActivity = async () => {
    if (!activityForm.contact_type || !activityForm.contact_result) {
      toast.error('Veuillez remplir le type et le résultat du contact');
      return;
    }

    setSaving(true);
    try {
      if (activityForm.new_stage) {
        // Change stage with activity
        await api.patch(`/opportunities/${selectedOpp.id}/stage`, {
          new_stage: activityForm.new_stage,
          contact_type: activityForm.contact_type,
          contact_result: activityForm.contact_result,
          notes: activityForm.notes,
          next_action_date: activityForm.next_action_date || null,
        });
        toast.success('Étape changée avec succès');
      } else {
        // Just log activity
        await api.post(`/opportunities/${selectedOpp.id}/log-activity`, {
          contact_type: activityForm.contact_type,
          contact_result: activityForm.contact_result,
          notes: activityForm.notes,
          next_action_date: activityForm.next_action_date || null,
        });
        toast.success('Activité enregistrée');
      }
      
      setActivityDialogOpen(false);
      fetchOpportunities();
      fetchDashboard();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (!closeForm.reason) {
      toast.error('Veuillez sélectionner une raison');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/opportunities/${selectedOpp.id}/close`, closeForm);
      toast.success('Opportunité clôturée');
      setCloseDialogOpen(false);
      fetchOpportunities();
      fetchDashboard();
    } catch (error) {
      toast.error('Erreur lors de la clôture');
    } finally {
      setSaving(false);
    }
  };

  const formatDelay = (hours) => {
    if (hours < 24) return `${hours}h de retard`;
    return `${Math.round(hours / 24)}j de retard`;
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleSendAlerts = async () => {
    try {
      const response = await api.post('/opportunities/alerts/check-overdue');
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des alertes');
    }
  };

  const handleSendSummary = async () => {
    try {
      const response = await api.post('/opportunities/alerts/send-summary');
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du récapitulatif');
    }
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      {/* Header + Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Pipeline Commercial
          </h1>
          <p className="text-slate-500 mt-1">
            Suivi des opportunités et relances
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'pipeline' && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Bell className="w-4 h-4 mr-2" />
                    Alertes
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleSendAlerts}>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Envoyer alertes retards
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSendSummary}>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer récapitulatif quotidien
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => { fetchOpportunities(); fetchDashboard(); }} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="flex border-b border-slate-200 -mt-2">
        <button
          data-testid="tab-pipeline"
          onClick={() => setActiveTab('pipeline')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'pipeline'
              ? 'border-slate-800 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Target className="w-4 h-4" />
          Pipeline
        </button>
        {isAdmin() && (
          <button
            data-testid="tab-rapport"
            onClick={() => setActiveTab('rapport')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'rapport'
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Rapport Commercial
          </button>
        )}
      </div>

      {/* Onglet Rapport Commercial (admin only) */}
      {activeTab === 'rapport' && isAdmin() && (
        <CommercialReportPage embedded />
      )}

      {/* Contenu Pipeline */}
      {activeTab === 'pipeline' && <>

      {/* Dashboard Stats */}
      {dashboard && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{dashboard.total_open}</div>
                    <div className="text-sm text-slate-500">En cours</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={dashboard.overdue_count > 0 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${dashboard.overdue_count > 0 ? 'bg-red-100' : 'bg-orange-100'}`}>
                    <AlertTriangle className={`w-5 h-5 ${dashboard.overdue_count > 0 ? 'text-red-600' : 'text-orange-600'}`} />
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${dashboard.overdue_count > 0 ? 'text-red-600' : 'text-slate-800'}`}>
                      {dashboard.overdue_count}
                    </div>
                    <div className="text-sm text-slate-500">En retard</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{dashboard.total_won}</div>
                    <div className="text-sm text-slate-500">Gagnées</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Euro className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-800">
                      {formatAmount(dashboard.pipeline_amount)}
                    </div>
                    <div className="text-sm text-slate-500">Pipeline</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Nouveaux KPIs */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xl font-bold text-emerald-600">{formatAmount(dashboard.ca_won_this_month || 0)}</span>
                      {dashboard.ca_won_prev_month > 0 && (
                        <span className={`text-xs font-medium ${(dashboard.ca_won_this_month || 0) >= dashboard.ca_won_prev_month ? 'text-emerald-500' : 'text-red-400'}`}>
                          {(dashboard.ca_won_this_month || 0) >= dashboard.ca_won_prev_month ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500">CA gagné ce mois</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{dashboard.conversion_rate || 0}%</div>
                    <div className="text-sm text-slate-500">Taux de conversion</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <Timer className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{dashboard.avg_closing_days || '—'}{dashboard.avg_closing_days ? 'j' : ''}</div>
                    <div className="text-sm text-slate-500">Délai moyen closing</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Activity className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-600">{dashboard.activities_this_week || 0}</div>
                    <div className="text-sm text-slate-500">Activités cette semaine</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pipeline Stages */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Pipeline par étape</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {dashboard.pipeline_stats.map((stat) => (
                  <button
                    key={stat.stage}
                    onClick={() => { setFilterStage(stat.stage); setFilterStatus('open'); }}
                    className={`flex-shrink-0 px-4 py-3 rounded-lg border-2 transition-all ${
                      filterStage === stat.stage 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="text-2xl font-bold" style={{ color: stat.color }}>{stat.count}</div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">{stat.label}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Overdue Alert */}
          {dashboard.overdue_count > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  {dashboard.overdue_count} opportunité(s) en retard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.overdue_opportunities.slice(0, 5).map((opp) => (
                    <div key={opp.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-red-500" />
                        <div>
                          <Link 
                            to={`/admin/opportunities/${opp.id}`}
                            className="font-medium text-slate-800 hover:text-orange-600 hover:underline"
                          >
                            {opp.client?.name || 'Client'}
                          </Link>
                          <p className="text-sm text-slate-500">{opp.stage_config?.label} - {formatDelay(opp.overdue_hours)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/admin/opportunities/${opp.id}`}>
                          <Button size="sm" variant="ghost">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button size="sm" variant="outline" onClick={() => openActivityDialog(opp, true)}>
                          <Phone className="w-4 h-4 mr-1" />
                          Traiter
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {dashboard.overdue_count > 5 && (
                  <Button 
                    variant="link" 
                    className="mt-2 text-red-600"
                    onClick={() => { setShowOverdueOnly(true); setFilterStage('all'); setFilterStatus('open'); }}
                  >
                    Voir toutes les opportunités en retard
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Filters + Search + View Toggle */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Recherche */}
        <div className="relative flex-1 min-w-48 max-w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un client, téléphone…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
            data-testid="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filterStage} onValueChange={(v) => { setFilterStage(v); setShowOverdueOnly(false); }}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Étape" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les étapes</SelectItem>
              {config.stages.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setShowOverdueOnly(false); }}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="open">En cours</SelectItem>
            <SelectItem value="won">Gagnées</SelectItem>
            <SelectItem value="lost">Perdues</SelectItem>
            <SelectItem value="archived">Archivées</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Checkbox 
            id="overdue" 
            checked={showOverdueOnly}
            onCheckedChange={(checked) => setShowOverdueOnly(checked)}
          />
          <Label htmlFor="overdue" className="text-sm text-slate-600">En retard uniquement</Label>
        </div>
        {/* Toggle vue Liste / Kanban */}
        <div className="ml-auto flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
          <button
            data-testid="view-list-btn"
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === 'list' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LayoutList className="w-4 h-4" /> Liste
          </button>
          <button
            data-testid="view-kanban-btn"
            onClick={() => setViewMode('kanban')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === 'kanban' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LayoutGrid className="w-4 h-4" /> Kanban
          </button>
        </div>
      </div>

      {/* Barre d'actions en masse */}
      {selectedIds.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="sticky top-2 z-30 flex items-center gap-3 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-xl"
        >
          <button onClick={() => setSelectedIds(new Set())} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium">
            {selectedIds.size} opportunité{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-slate-700 gap-2"
            onClick={() => handleBulkAction('reopen')}
            disabled={bulkLoading}
            data-testid="bulk-reopen-btn"
          >
            <RotateCcw className="w-4 h-4" />
            Rouvrir
          </Button>
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
            onClick={() => handleBulkAction('archive')}
            disabled={bulkLoading}
            data-testid="bulk-archive-btn"
          >
            {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
            Archiver
          </Button>
          {isAdmin() && (
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              onClick={() => handleBulkAction('delete')}
              disabled={bulkLoading}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </Button>
          )}
        </div>
      )}

      {/* Vue Kanban */}
      {viewMode === 'kanban' && !loading && (
        <div data-testid="kanban-view" className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {config.stages.map(stage => {
              const stageOpps = filteredOpportunities.filter(o => o.stage === stage.id);
              const stageAmount = stageOpps.reduce((s, o) => s + (o.estimated_amount || 0), 0);
              return (
                <div key={stage.id} className="w-64 flex-shrink-0">
                  {/* Entête colonne */}
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                      <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{stage.label}</span>
                    </div>
                    <span className="text-xs font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{stageOpps.length}</span>
                  </div>
                  {stageAmount > 0 && (
                    <div className="text-xs text-slate-400 px-1 mb-2">{formatAmount(stageAmount)}</div>
                  )}
                  {/* Cartes */}
                  <div className="space-y-2">
                    {stageOpps.length === 0 && (
                      <div className="border-2 border-dashed border-slate-200 rounded-lg h-16 flex items-center justify-center">
                        <span className="text-xs text-slate-300">Vide</span>
                      </div>
                    )}
                    {stageOpps.map(opp => (
                      <Link
                        key={opp.id}
                        to={`/admin/opportunities/${opp.id}`}
                        className={`block bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${opp.is_overdue ? 'border-red-200 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`kanban-card-${opp.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <span className="text-sm font-semibold text-slate-800 leading-tight">{opp.client?.name || 'Client'}</span>
                          {opp.is_overdue && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium">{formatDelay(opp.overdue_hours)}</span>}
                        </div>
                        {opp.estimated_amount > 0 && (
                          <div className="text-xs font-medium text-slate-500 mb-1.5">{formatAmount(opp.estimated_amount)}</div>
                        )}
                        <div className="flex items-center justify-between">
                          {opp.client?.phone && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" />{opp.client.phone}
                            </span>
                          )}
                          {opp.devis_count > 0 && (
                            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{opp.devis_count} devis</span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vue Liste */}
      {viewMode === 'list' && (
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : filteredOpportunities.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>{searchQuery ? `Aucun résultat pour "${searchQuery}"` : 'Aucune opportunité trouvée'}</p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Select all header */}
              <div className="px-4 py-2 bg-slate-50 flex items-center gap-3 border-b border-slate-100">
                <Checkbox
                  data-testid="select-all-checkbox"
                  checked={filteredOpportunities.length > 0 && selectedIds.size === filteredOpportunities.length}
                  onCheckedChange={toggleSelectAll}
                  className="data-[state=checked]:bg-slate-800 data-[state=checked]:border-slate-800"
                />
                <span className="text-xs text-slate-500">
                  {selectedIds.size > 0
                    ? `${selectedIds.size} / ${filteredOpportunities.length} sélectionnée(s)`
                    : `Tout sélectionner (${filteredOpportunities.length})`}
                </span>
              </div>
              {filteredOpportunities.map((opp) => (
                <div
                  key={opp.id}
                  className={`p-4 hover:bg-slate-50 transition-colors ${opp.is_overdue ? 'bg-red-50' : ''} ${selectedIds.has(opp.id) ? 'bg-blue-50 border-l-2 border-blue-400' : ''}`}
                  data-testid={`opp-item-${opp.id}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Checkbox */}
                      <Checkbox
                        checked={selectedIds.has(opp.id)}
                        onCheckedChange={() => toggleSelect(opp.id)}
                        data-testid={`checkbox-${opp.id}`}
                        className="flex-shrink-0 data-[state=checked]:bg-slate-800 data-[state=checked]:border-slate-800"
                      />
                      {/* Status indicator */}
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: opp.stage_config?.color || '#6B7280' }}
                      />
                      
                      {/* Client info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Link 
                            to={`/admin/opportunities/${opp.id}`}
                            className="font-semibold text-slate-800 hover:text-orange-600 truncate"
                          >
                            {opp.client?.name || 'Client'}
                          </Link>
                          {opp.is_overdue && (
                            <Badge variant="destructive" className="text-xs">
                              {formatDelay(opp.overdue_hours)}
                            </Badge>
                          )}
                          {opp.status === 'won' && (
                            <Badge className="bg-green-100 text-green-700">Gagné</Badge>
                          )}
                          {opp.status === 'lost' && (
                            <Badge variant="secondary">Perdu</Badge>
                          )}
                          {opp.status === 'archived' && (
                            <Badge className="bg-slate-100 text-slate-500 flex items-center gap-1">
                              <Archive className="w-3 h-3" />Archivé
                            </Badge>
                          )}
                          {/* Financing indicator */}
                          {opp.financing && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              <span className="font-medium">{opp.financing_progress}</span>
                              <span className="ml-1 text-purple-500">financement</span>
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <span 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: opp.stage_config?.color }}
                            />
                            {opp.stage_config?.label}
                          </span>
                          {opp.devis_count > 0 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <span className="font-medium">{opp.devis_count}</span> devis
                            </span>
                          )}
                          {opp.client?.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {opp.client.phone}
                            </span>
                          )}
                          {(opp.total_amount > 0 || opp.estimated_amount > 0) && (
                            <span className="font-medium text-green-600">
                              {formatAmount(opp.total_amount || opp.estimated_amount)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Financing Progress (if present) */}
                      {opp.financing && (
                        <div className="hidden lg:block w-24">
                          <Progress value={opp.financing.progress_percent} className="h-2 bg-purple-100" />
                          <p className="text-xs text-purple-500 mt-1 text-center">
                            {opp.financing.progress_percent}% financement
                          </p>
                        </div>
                      )}

                      {/* Progress bar */}
                      <div className="hidden md:block w-32">
                        <Progress value={opp.progress} className="h-2" />
                        <p className="text-xs text-slate-400 mt-1 text-center">{opp.progress}%</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {/* View Detail Button */}
                      <Link to={`/admin/opportunities/${opp.id}`}>
                        <Button size="sm" variant="ghost" className="text-slate-600">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      {opp.status === 'open' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openActivityDialog(opp)}>
                            <Phone className="w-4 h-4 mr-1" />
                            Contact
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-orange-500 hover:bg-orange-600"
                            onClick={() => openActivityDialog(opp, true)}
                          >
                            <ArrowRight className="w-4 h-4 mr-1" />
                            Avancer
                          </Button>
                        </>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/clients/${opp.client_id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir le client
                            </Link>
                          </DropdownMenuItem>
                          {opp.status === 'open' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openCloseDialog(opp)} className="text-green-600">
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Marquer gagné
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openCloseDialog(opp)} className="text-red-600">
                                <XCircle className="w-4 h-4 mr-2" />
                                Marquer perdu
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )} {/* fin vue liste */}

      {/* Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {activityForm.new_stage ? 'Avancer l\'opportunité' : 'Enregistrer un contact'}
            </DialogTitle>
            <DialogDescription>
              {selectedOpp?.client?.name} - {selectedOpp?.stage_config?.label}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {activityForm.new_stage && (
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-700">
                  <ArrowRight className="w-4 h-4 inline mr-1" />
                  Passage à l'étape: <strong>{config.stages.find(s => s.id === activityForm.new_stage)?.label}</strong>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Type de contact *</Label>
              <Select 
                value={activityForm.contact_type}
                onValueChange={(v) => setActivityForm(f => ({ ...f, contact_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {config.contact_types.map((type) => {
                    const Icon = CONTACT_ICONS[type.id] || Phone;
                    return (
                      <SelectItem key={type.id} value={type.id}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {type.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Résultat *</Label>
              <Select 
                value={activityForm.contact_result}
                onValueChange={(v) => setActivityForm(f => ({ ...f, contact_result: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {config.contact_results.map((result) => (
                    <SelectItem key={result.id} value={result.id}>
                      {result.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Prochaine action</Label>
              <Input
                type="date"
                value={activityForm.next_action_date}
                onChange={(e) => setActivityForm(f => ({ ...f, next_action_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={activityForm.notes}
                onChange={(e) => setActivityForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes sur l'échange..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleLogActivity} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clôturer l'opportunité</DialogTitle>
            <DialogDescription>
              {selectedOpp?.client?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Raison de clôture *</Label>
              <Select 
                value={closeForm.reason}
                onValueChange={(v) => setCloseForm(f => ({ ...f, reason: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {config.close_reasons.map((reason) => (
                    <SelectItem key={reason.id} value={reason.id}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={closeForm.notes}
                onChange={(e) => setCloseForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Commentaires sur la clôture..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleClose} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Clôturer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </> /* fin onglet pipeline */}
    </div>
  );
};

export default OpportunitiesPage;
